import { type Country, dateFormats, useCountries, useFormatAmount, useIntl } from '@capsicum/common';
import { type BidderRowRPProps } from '@capsicum/features/fanshop/shared';
import styled from 'styled-components';

import { ShippingFlag } from '../shipping-flag/ShippingFlag';

export const BidderRowRP = ({ amount, anonymizedName, createdAt, iso2Country, isOwnBid }: BidderRowRPProps) => {
	const { getCountryBy, getCountryFlagUri } = useCountries();
	const { $t } = useIntl();
	const { formatAmount } = useFormatAmount();
	const country = getCountryBy(iso2Country?.toUpperCase(), 'iso2') as Country;

	const bidderName = (
		<S.StyledTextBold $isOwnBid={isOwnBid}>
			{isOwnBid
				? $t('rewards.rpAuctions.bidderRow.you').toUpperCase()
				: `${$t('rewards.rpAuctions.bidderRow.user')} ${anonymizedName}`}
		</S.StyledTextBold>
	);

	const formattedDate = dateFormats.format(createdAt, 'DD MMMM YYYY HH:mm:ss');

	return (
		<S.Layout $isOwnBid={isOwnBid}>
			<ShippingFlag url={getCountryFlagUri(country)} />
			<S.TextContainer>
				<div>
					{bidderName}
					<S.StyledText>{formattedDate}</S.StyledText>
				</div>
				<S.AmountWrapper>
					<S.StyledAmount>{`${formatAmount(amount)}`}</S.StyledAmount>
				</S.AmountWrapper>
			</S.TextContainer>
		</S.Layout>
	);
};

namespace S {
	export const Layout = styled.div<{ $isOwnBid: boolean }>`
		display: flex;
		padding: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.border.radius.medium};
		background-color: ${({ $isOwnBid, theme }) =>
			$isOwnBid ? theme.colors.backgrounds.info.subtle : theme.colors.backgrounds.main.container};
		flex-direction: row;
		align-items: center;
	`;

	export const TextContainer = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		flex: 1;
		margin-left: ${({ theme }) => theme.spacings.small};
	`;

	export const StyledTextBold = styled.div<{ $isOwnBid: boolean }>`
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ $isOwnBid, theme }) =>
			$isOwnBid ? theme.colors.text.brand.enabled : theme.colors.text.main.enabled};
	`;

	export const StyledText = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const AmountWrapper = styled.div`
		display: flex;
	`;

	export const StyledAmount = styled.div`
		${({ theme }) => theme.fonts.text.text1};
		text-align: right;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin: auto;
	`;
}
